// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## RhythmChecker
//
// Compares a performance of a rhythm to a music voice rhythm.
import { StaveNote } from 'vexflow';
import Voice from '../uTheory.Notation/voice';
import TablaInstrument from '../uTheory.Notation/tablaInstrument';
import utils from '../utils';
import RhythmCheck from '../uTheory.Vex/rhythmCheck';
import * as Skills from '../uTheory.Skills/skills';
import Pointer from '../uTheory.Notation/pointer';
import type MusicalClock from '../uTheory.Notation/musicalClock';
import type Renderer from '../uTheory.Notation/renderer';
import type { NotePointers } from '../uTheory.Notation/measure';
import type NoteHolder from '../uTheory.Notation/noteHolder';
import type { RhythmSkillsGroup } from '../uTheory.Skills/processRhythm';
import type { RecorderEvent } from '../uTheory.Notation/recorder';

export interface SkillsAndAccuracy {
  average: number;
  skills: RhythmSkillAccuracy[];
}

export interface RhythmSkillAccuracy {
  skill: number;
  textId: string;
  questions: number;
  numberOfNotes: number;
  numberOfSoundedNotes: number;
  n: number;
  average: number;
}

// GCR TODO: Move to recorder.js
export interface RecorderEventWithHit extends RecorderEvent {
  hit?: Hit;
}

export interface Hit {
  /** target.beats - actual.beats */
  differenceBeats: number;
  absDifferenceBeats: number;
  answerPointersIndex: number;
  accuracy?: Accuracy;
  /** Has a previous played note been matched to this answerPointer? */
  taken: boolean;
  /** Has this answerPointer been previously marked as missed (not played)? */
  missed: boolean;
  /** The noteHolder of the answerPointer object  */
  noteHolder: NoteHolder;
  // pointer: target.startPointer,
  stopPointer: Pointer;
  evt?: RecorderEventWithHit;
  rc?: RhythmCheck;
}

interface Accuracy {
  /** 0-100 percent */
  percent: number;
  /** 0-100 percent */
  seconds: number;
  /** 0-100 percent */
  deviation: number | undefined;
}

export interface AnswerPointers extends NotePointers {
  hit?: Hit;
  miss?: boolean | Hit;
}

interface RhythmCheckerOptions {
  playSounds?: boolean;
  voice?: Voice;
}

interface CheckData {
  // Reset check variables & data:
  nextExpectedNote: number;
  voicePointers: AnswerPointers[];
  events: RecorderEventWithHit[];
  misses: (boolean | Hit)[];
  visualFeedbacks: RhythmCheck[];
  veryEarly: number;
  veryLate: number;
  omittedNotes: number;
}

// Helper function:
// Takes a pointer and compares it to a list of events for the
// nearest 2 hits:
const findHits = (needle: Pointer, answerPointers: AnswerPointers[]): Hit[] => {
  const beats = needle.toBeats();
  let greaterThan = 0;

  // Find the nearest hits by searching up to and just beyond the needle:
  const hits = [];
  for (let i = 0; i < answerPointers.length; i++) {
    const target = answerPointers[i];
    if (!target.hasOwnProperty('beats')) {
      target.beats = target.startPointer.toBeats();
    }
    const hit: Hit = {
      differenceBeats: target.beats - beats,
      absDifferenceBeats: Math.abs(target.beats - beats),
      answerPointersIndex: i,
      taken: target.hasOwnProperty('hit') && typeof target.hit !== 'undefined',
      missed: target.hasOwnProperty('miss') && target.miss === true,
      noteHolder: target.noteHolder,
      // pointer: target.startPointer,
      stopPointer: target.stopPointer, // critical for playback
    };
    hits.push(hit);
    // We've got our two closest when we've looked past the needle
    // by at least 2 items.
    if (target.beats > beats && hits.length >= 2 && greaterThan++ >= 1) {
      break;
    }
  }

  hits.sort((a, b) => {
    if (Math.abs(a.differenceBeats) < Math.abs(b.differenceBeats)) {
      return -1;
    }
    return 1;
  });
  return hits;
};

export default class RhythmChecker {
  clock: MusicalClock;

  /** max deviation for partial credit */
  toleranceSeconds = 0.2;

  /** max deviation for 100% accuracy */
  maxPerfectToleranceSeconds = 0.05;

  targetPercent = 90;

  voice: Voice;

  checkData: CheckData;

  options: RhythmCheckerOptions;

  renderer: Renderer;

  tablas: TablaInstrument;

  debug = false;

  log(...args) {
    if (this.debug) console.log(...args);
  }

  constructor(musicalClock: MusicalClock, options: RhythmCheckerOptions = {}) {
    this.clock = musicalClock;
    this.options = options;

    // Voice doesn't have to be defined on init, can be set later
    // with setVoice();
    if (typeof options.voice !== 'undefined' && options.voice instanceof Voice) {
      this.voice = options.voice;
    }

    // Enable live sounds as student plays with playSounds.
    if (options.playSounds) this.initTablas();

    // Initialize variables specific to one checking:
    this.reset();
  }

  reset() {
    this.checkData = {
      // Reset check variables & data:
      nextExpectedNote: 0,
      voicePointers: [],
      events: [],
      misses: [],
      visualFeedbacks: [],
      veryEarly: 0,
      veryLate: 0,
      omittedNotes: 0,
    };
  }

  setVoice(voice: Voice) {
    if (!(voice instanceof Voice)) {
      throw new Error(
        'Not a Voice: RhythmChecker.setVoice must take an instance of Voice as its argument.'
      );
    }
    this.voice = voice;
  }

  setRenderer(renderer: Renderer) {
    this.renderer = renderer;
  }

  check(events: RecorderEventWithHit[]) {
    this.reset();
    // Process the events:
    for (let i = 0; i < events.length; i++) {
      this.checkEvent(events[i]);
    }
  }

  checkEvent(evt: RecorderEventWithHit) {
    // Recorder sends an extra event at very end w/ a final note off
    // or a final rest to real time callbacks.  Ignore it, if needed.
    if (
      evt.midiNumber === 'r' ||
      (this.checkData.events.length > 0 &&
        evt.performanceStartPointer.equals(
          this.checkData.events[this.checkData.events.length - 1].performanceStartPointer
        ))
    ) {
      return;
    }

    this.checkData.events.push(evt);

    this.log(`\n\nNew Event:${this.checkData.events.length}`);
    this.log(evt);

    const haystack = this.getVoicePointers();

    // Find the closest hits for the event:
    const hits = findHits(evt.performanceStartPointer, haystack);

    // Max tolerance for calculating hits:

    // Calculate tolerance in terms of beats:
    const tolerance = this.toleranceSeconds / this.clock.beatSec;
    // console.log('toleranceSeconds', this.toleranceSeconds, 'tolerance', tolerance);

    this.playSound(evt, hits, tolerance);

    this.log('Hits:');
    this.log(hits);

    // Is it a normal accurate hit?
    if (this.nextExpectedIsClosest(hits, evt, tolerance)) return;

    // Maybe, just maybe, we counted something earlier as a skipped note
    // that we should have counted as a hit.
    if (this.previousNoteWasNotActuallySkipped(hits, evt, tolerance)) return;

    // Or maybe we dragged/rushed a bit?
    if (this.nextExpectedIsSecondClosest(hits, evt, tolerance)) return;

    // At this point we know the event did not hit the next expected note,
    // so there is definitely a user error of some sort.  Let's figure
    // out what's gone wrong.
    if (this.hitIsNotNextExpected(hits, evt, tolerance)) return;

    // Maybe the previous "hit" didn't deserve to be one. Let's check:
    if (this.thePreviousHitWasBad(hits, evt, tolerance)) return;

    // If we've gotten this far, it's NOT in range of any note:
    // Call it a miss and process it!
    this.miss(evt, hits[0]);
    // Mark missed note(s) as missed, update expected pointer
    this.markMissed(evt);
  }

  nextExpectedIsClosest(hits: Hit[], evt: RecorderEventWithHit, beatTolerance: number) {
    // If the closest note is the next expected note,
    const closestIsNextExpected = hits[0].answerPointersIndex === this.checkData.nextExpectedNote;
    // and the next expected note is not taken:
    const notYetTaken = !hits[0].taken;
    // and it's within tolearnce, we have a hit:
    const withinTolerance = hits[0].absDifferenceBeats <= beatTolerance;
    // then calculate accuracy and update the haystack image

    // console.log(
    //   'closestIsNextExpected',
    //   closestIsNextExpected,
    //   'notYetTaken',
    //   notYetTaken,
    //   'withinTolerance',
    //   withinTolerance
    // );

    if (closestIsNextExpected && notYetTaken && withinTolerance) {
      this.hit(hits[0], evt);
      this.checkData.nextExpectedNote++;
      this.log('nextExpectedIsClosest');
      return true;
    }
    return false;
  }

  previousNoteWasNotActuallySkipped(hits: Hit[], evt: RecorderEventWithHit, beatTolerance: number) {
    this.log('Trying previousNoteWasNotActuallySkipped');
    // evt, priorEvt, priorIndex = priorEvt.hit.index

    // If there's no prior note, move on!
    if (this.checkData.events.length < 2) return false;

    const priorEvt = this.checkData.events[this.checkData.events.length - 2];

    // Did the prior event register as hitting nothing? Move on!
    if (typeof priorEvt.hit === 'undefined') return false;

    const priorIndex = priorEvt.hit.answerPointersIndex;

    // If there's no note before the previous hit, return...
    if (priorIndex === 0) return false;

    this.log('  initial tests passed.');

    const pointers = this.getVoicePointers();

    // If the note before the last hit was missed:
    const noteBeforeLastHitMissed = pointers[priorIndex - 1].miss === true;
    // And the closest hit was not taken:
    const closestHitTaken = hits[0].taken;
    // And the two notes under consideration are within beat tolerance of each other
    const bothNotesWithinTolerance =
      Math.abs(
        // |(evt1 - pointer1) - (evt2 - pointer2)| < tolerance
        priorEvt.performanceStartPointer
          .clone()
          .subtract(pointers[priorIndex - 1].startPointer)
          .subtract(evt.performanceStartPointer)
          .add(pointers[priorIndex].startPointer)
          .toBeats()
      ) <= beatTolerance;

    if (noteBeforeLastHitMissed && closestHitTaken && bothNotesWithinTolerance) {
      // Remove the priorEvt's hit:
      delete pointers[priorIndex].hit;
      this.deleteVisualFeedback(priorEvt.hit.rc);
      priorEvt.hit.evt = undefined;
      priorEvt.hit = undefined;
      // Remove the miss marking:
      delete pointers[priorIndex - 1].miss;
      this.checkData.visualFeedbacks.forEach((vf) => {
        if (vf.noteHolder === pointers[priorIndex - 1].noteHolder) {
          this.deleteVisualFeedback(vf);
        }
      }, this);

      // Attach the priorEvt to the missed note...
      const priorEvtHits = findHits(priorEvt.performanceStartPointer, pointers);
      let i;
      for (i = 0; i < priorEvtHits.length; i++) {
        if (priorEvtHits[i].answerPointersIndex === priorIndex - 1) break;
      }
      this.hit(priorEvtHits[i], priorEvt);
      // And attach this evt to the priorIndex
      for (i = 0; i < hits.length; i++) {
        if (hits[i].answerPointersIndex === priorIndex) break;
      }
      this.hit(hits[i], evt);
      this.redrawFeedback();
      this.log('previousNoteWasNotActuallySkipped');
      return true;
    }
    return false;
  }

  nextExpectedIsSecondClosest(hits: Hit[], evt: RecorderEventWithHit, beatTolerance: number) {
    // If the closest hit is NOT the next expected note, see if it might
    // actually be just a bit late or early...
    if (
      hits[0].answerPointersIndex !== this.checkData.nextExpectedNote &&
      // Is the NEXT closest note the expected note?
      hits[1].answerPointersIndex === this.checkData.nextExpectedNote &&
      // And is it not taken?
      !hits[1].taken &&
      // If so, is it within 0.2s difference?
      Math.abs(hits[1].absDifferenceBeats) <= beatTolerance &&
      // And is it conceivably a hit?
      this.calculateAccuracy(hits[1], evt).percent > 70 &&
      // And we're not pulling it away from something that's
      // actually REALLY accurate, are we?
      this.calculateAccuracy(hits[0], evt).seconds < 98.5
    ) {
      //        this.log("I'm a hit...");
      //        this.log("h0 differenceBeats: " + hits[0].differenceBeats);
      //        this.log("h1 differenceBeats: " + hits[1].differenceBeats);

      const hit =
        hits[1].answerPointersIndex === this.checkData.nextExpectedNote ? hits[1] : hits[2];

      // Then call it a hit and process it!
      this.hit(hit, evt);
      this.checkData.nextExpectedNote++;
      this.log('nextExpectedIsSecondClosest');
      return true;
    }
    return false;
  }

  hitIsNotNextExpected(hits: Hit[], evt: RecorderEventWithHit, beatTolerance: number) {
    // Is the closest note within tolerance range of a hit? (0.2s?)
    if (
      hits[0].absDifferenceBeats <= beatTolerance &&
      // And has that note not yet been hit?
      !hits[0].taken
    ) {
      this.log('Hits 3');
      // Update the next expected note:
      this.checkData.nextExpectedNote = hits[0].answerPointersIndex + 1;

      // Call it a hit and process it!
      this.hit(hits[0], evt);
      // Mark missed note(s) as missed, update expected pointer
      this.markMissed(evt);
      this.log('hitIsNotNextExpected');
      return true;
    }
    return false;
  }

  thePreviousHitWasBad(hits: Hit[], evt: RecorderEventWithHit, beatTolerance: number) {
    const haystack = this.getVoicePointers();
    // Is the closest note within tolerance
    if (
      hits[0].absDifferenceBeats <= beatTolerance &&
      // Already taken
      hits[0].taken &&
      // And this hit is better accuracy?
      this.calculateAccuracy(hits[0], evt).percent >
        haystack[hits[0].answerPointersIndex].hit.accuracy.percent
    ) {
      // Undo the previous hit & call it a miss:
      // Redraw the visual feedback:
      const prevHit = haystack[hits[0].answerPointersIndex].hit;
      this.removeHit(prevHit);

      // Call this a hit:
      this.hit(hits[0], evt);
      this.markMissed(evt);
      this.log('thePreviousHitWasBad');
      return true;
    }
    return false;
  }

  hit(hit: Hit, evt: RecorderEventWithHit) {
    // Log it in the voicePointers & events object:
    const pointers = this.getVoicePointers();

    // Wire everything up to each other:
    pointers[hit.answerPointersIndex].hit = hit;
    evt.hit = hit;
    hit.evt = evt;

    // Remove any previous misses:
    this.removeMisses(hit);

    // Calculate the accuracy of the hit:
    hit.accuracy = this.calculateAccuracy(hit, evt);

    // Adjust accuracy of prior by looking at ratio between notes /
    // rhythmic prolation for the last three events:
    this.prolationCheck();

    this.showHitFeedback(hit, evt);
  }

  prolationCheck() {
    // Are there at least two previous events?
    if (this.checkData.events.length < 3) return;
    const events = this.checkData.events.slice(-3);

    // Are both previous ones hits notes in the voice?
    // And do they hit adjacent notes in the voice?
    if (
      !(
        typeof events[0].hit !== 'undefined' &&
        typeof events[1].hit !== 'undefined' &&
        typeof events[2].hit !== 'undefined'
      ) ||
      !(
        events[0].hit.answerPointersIndex === events[1].hit.answerPointersIndex - 1 &&
        events[1].hit.answerPointersIndex === events[2].hit.answerPointersIndex - 1
      )
    ) {
      return;
    }
    // Are the first & last accurate enough that it
    // makes sense to calculate a prolation based on them?
    if (events[0].hit.accuracy.percent < 75 || events[2].hit.accuracy.percent < 75) return;

    //
    // Calculate the expected ratio:
    //
    const expected = [];
    const pointers = this.getVoicePointers().slice(
      events[0].hit.answerPointersIndex,
      events[0].hit.answerPointersIndex + 3
    );
    // If the note we're re-calculating begins on a beat,
    // don't recalculate it -- better that they correctly came
    // in on the beat than that their ratio to the prior note was
    // correct:
    if (pointers[1].startPointer.s === 0) return;

    // First note to second note expected length:
    expected[0] = pointers[1].startPointer.clone().subtract(pointers[0].startPointer).toBeats();
    // Second to third expected length:
    expected[1] = pointers[2].startPointer.clone().subtract(pointers[1].startPointer).toBeats();

    // Expected ratio:
    const expectedRatio = expected[0] / expected[1];

    //
    // Calculate the actual ratio:
    //
    const actual = [];
    // First note to second note actual length:
    actual[0] = events[1].performanceStartPointer
      .clone()
      .subtract(events[0].performanceStartPointer)
      .toBeats();
    // Second to third actual length:
    actual[1] = events[2].performanceStartPointer
      .clone()
      .subtract(events[1].performanceStartPointer)
      .toBeats();
    // Actual ratio:
    const actualRatio = actual[0] / actual[1];

    //
    // Calculate the prolation accuracy of the 2nd note:
    //
    let prolationAccuracy = 100 - Math.abs((100 * (expectedRatio - actualRatio)) / actualRatio);

    this.log(`Raw prolation accuracy: ${prolationAccuracy}`);

    prolationAccuracy = Math.max(0, prolationAccuracy);
    prolationAccuracy = Math.min(100, prolationAccuracy);

    //
    // Adjust the accuracy of the second note to reflect prolation:
    //
    const priorAccuracy = events[1].hit.accuracy.percent;

    // Take calculated accuracy (0.5 * timing + 0.5 * duration)
    // And make the new accuracy 0.66 * calculated + 0.34 * prolation
    const newAccuracy = 0.66 * priorAccuracy + 0.34 * prolationAccuracy;
    this.log(`Index: ${events[1].hit.answerPointersIndex} prolationAccuracy: ${prolationAccuracy}`);

    // The prolation accuracy of the second note is adjusted:
    events[1].hit.accuracy.percent = newAccuracy;

    // If the change is significant:
    if (Math.abs(priorAccuracy - newAccuracy) > 5) {
      this.log('Prolation changed things:');
      this.log(events[1].hit);
      // Remove the prior visual feebdack:
      this.deleteVisualFeedback(events[1].hit.rc);
      // And make a new visual feedback:
      this.showHitFeedback(events[1].hit, events[1]);
      // And redraw it all:
      this.redrawFeedback();
      // And we're done!
    }
  }

  deleteVisualFeedback(rc: RhythmCheck) {
    for (let i = 0; i < this.checkData.visualFeedbacks.length; i++) {
      if (this.checkData.visualFeedbacks[i] === rc) {
        this.checkData.visualFeedbacks.splice(i, 1);
        this.log(`Deleting rc for ${i}`);
      }
    }
  }

  removeHit(hit: Hit) {
    // Remove the visual feedback:
    const rc = hit.rc;
    this.deleteVisualFeedback(rc);

    // Redraw it as a miss:
    hit.evt.hit = undefined;
    this.miss(hit.evt, hit);

    // Redraw it all:
    this.redrawFeedback();

    // Unwire hit.evt, evt.hit:
    hit.evt = undefined;
  }

  redrawFeedback() {
    // If we have a renderer, let's redraw:
    if (typeof this.renderer !== 'undefined') {
      // this.renderer.redraw();
      this.checkData.visualFeedbacks.forEach((rc) => {
        rc.draw();
      });
    }
  }

  showHitFeedback(hit, evt) {
    // If we're not in a rendered context, don't show any feedback.
    if (!hit.noteHolder.hasOwnProperty('staveNote')) return;

    // Otherwise, show it!
    const toHex = function (c) {
      //          this.log("converting color #: " + c);
      if (c > 255 || c < 0) {
        throw new Error(`Not a Color: Error! color #: ${c}`);
      }
      const hex = c.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    };

    this.log('Hit accuracy:', hit.accuracy);

    // Display visual feedback for the user:
    // If accuracy >= 95%, check mark.
    let rc;
    if (hit.accuracy.percent >= this.targetPercent) {
      rc = new RhythmCheck(hit.noteHolder, 'green');
    } else {
      let r;
      let g;
      const accuracy = hit.accuracy.percent;
      // Otherwise calculate color:
      // Gradient, 3 stop-points:
      // @100%: (0, 163, 0)
      // @50%: (239, 211, 0)
      // @0%: (191, 0, 0)

      // Or 2 stop-points:
      // @100%: (0, 200, 0)
      // @80%: (255, 200, 0)
      // @50%: (255, 0, 0)
      if (accuracy > 80) {
        r = ~~(-12.75 * accuracy + 1275);
        g = 200;
      } else {
        r = 255;
        g = ~~((20 * accuracy - 1000) / 3);
        g = Math.max(0, g);
      }

      const b = '00';

      const color = `#${toHex(r)}${toHex(g)}${b}`;
      this.log(`Color: ${color}`);

      const early = hit.differenceBeats > 0;
      //        var very = hit.accuracy < 30;
      const very = false;
      const statusString = early ? (very ? 'veryEarly' : 'early') : very ? 'veryLate' : 'late';

      const offsetObj = this.calculateX(evt, hit);

      if (offsetObj.offset) {
        const rcOptions = { color, x: offsetObj.x };
        rc = new RhythmCheck(hit.noteHolder, statusString, rcOptions);
      } else {
        rc = new RhythmCheck(hit.noteHolder, statusString, { color });
      }
    }
    rc.draw();
    hit.rc = rc;
    this.checkData.visualFeedbacks.push(rc);
  }

  miss(evt, hit) {
    // Remove any previous misses:
    this.removeMisses(hit);
    hit = utils.clone(hit);
    evt = utils.clone(evt);

    this.showMissFeedback(evt, hit);
    this.log('Miss!');
  }

  removeMisses(hit) {
    // If there were any previous misses, remove them:
    const pointers = this.getVoicePointers();
    const thisHitPointer = pointers[hit.answerPointersIndex];

    let redrawNeeded = false;
    if (typeof thisHitPointer.miss === 'object' && thisHitPointer.miss.hasOwnProperty('rc')) {
      //          this.log("Disconnecting Line!");
      redrawNeeded = thisHitPointer.miss.rc.disconnectLine() || redrawNeeded;
      this.checkData.misses.push(thisHitPointer.miss);
      thisHitPointer.miss = undefined;
    }
    if (redrawNeeded && typeof this.renderer !== 'undefined') {
      // this.renderer.redraw();
      this.checkData.visualFeedbacks.forEach((rc) => {
        rc.draw();
      });
    }
  }

  calculateX(
    evt: RecorderEventWithHit,
    hit: Hit
  ): {
    x?: number;
    noteHolder?: NoteHolder;
    offset: boolean;
    afterIndex: number;
  } {
    const p = this.getVoicePointers();
    const afterIndex = Pointer.afterIndex(
      evt.performanceStartPointer, // needle
      p, // haystack
      'startPointer' // haystack object property name
    );

    // If it's within range:
    if (afterIndex >= 0 && afterIndex < p.length - 1) {
      // Calculate distance between the two pointers:
      const startStaveNote = p[afterIndex].noteHolder.staveNote;
      const endStaveNote = p[afterIndex + 1].noteHolder.staveNote;
      if (!(startStaveNote instanceof StaveNote) || !(endStaveNote instanceof StaveNote)) {
        throw new Error('RhythmChecker: Cannot calculate startX and endX from ghost notes!');
      }
      const startX = startStaveNote.getNoteHeadBeginX();
      const endX = endStaveNote.getNoteHeadBeginX();
      const width = endX - startX;

      // Determine musical distance between these:
      const startBeat = p[afterIndex].startPointer.toBeats();
      const endBeat = p[afterIndex + 1].startPointer.toBeats();
      const evtBeat = evt.performanceStartPointer.toBeats() - startBeat;

      const widthBeats = endBeat - startBeat;
      const evtPercent = evtBeat / widthBeats;

      let x = startX + width * evtPercent;

      // Calculate offset:
      const minVisualOffset = 7; // what's visible to the eye...
      const staveNote = p[hit.answerPointersIndex].noteHolder.staveNote;
      if (!(staveNote instanceof StaveNote))
        throw new Error('RhythmChecker: Cannot calculate x from a ghost note!');
      const staveNoteX = staveNote.getNoteHeadBeginX();
      if (Math.abs(staveNoteX - x) < minVisualOffset) {
        x = x < staveNoteX ? staveNoteX - minVisualOffset : staveNoteX + minVisualOffset;
      }

      return {
        x,
        afterIndex,
        noteHolder: p[hit.answerPointersIndex].noteHolder,
        offset: true,
      };
    }
    return { afterIndex, offset: false };
  }

  showMissFeedback(evt, hit) {
    let rc;
    let rcOptions;
    let label;
    let x;
    const p = this.getVoicePointers();
    let index;

    const offset = this.calculateX(evt, hit);
    if (offset.offset) {
      x = offset.x;
      index = offset.afterIndex;
      const early = hit.differenceBeats > 0;
      // If they're both taken, treat it as very late:
      // Don't draw a connector line.
      if ((p[index].hit || p[index].miss) && (p[index + 1].hit || p[index + 1].miss)) {
        label = 'veryLate';

        // Otherwise, if the closest is not taken, go with it:
      } else if (!(p[hit.answerPointersIndex].hit || p[hit.answerPointersIndex].miss)) {
        index = hit.answerPointersIndex;
        if (early) {
          label = 'early';
        } else {
          label = 'late';
        }
        // Go with the other one:
      } else {
        index = early ? offset.afterIndex : offset.afterIndex + 1;
        label = early ? 'late' : 'early';
      }
      rcOptions = { x, color: 'red' };

      // Create the visual feedback:
      rc = new RhythmCheck(p[index].noteHolder, label, rcOptions);
      // It's before the first or after the last, do something special.
    } else if (offset.afterIndex < 0) {
      // it's early:
      index = 0;
      this.checkData.veryEarly++;
      rcOptions = { offset: this.checkData.veryEarly * -31 };
      rc = new RhythmCheck(p[0].noteHolder, 'veryEarly', rcOptions);
    } else {
      // it's late:
      this.checkData.veryLate++;
      index = p.length - 1;
      rcOptions = { offset: this.checkData.veryLate * 31 };
      rc = new RhythmCheck(p[p.length - 1].noteHolder, 'veryLate', rcOptions);
    }

    // Update the index of the hit -- so we can redraw
    // as needed:
    hit.answerPointersIndex = index;
    hit.evt = utils.clone(evt || hit.evt);

    if (!hit.evt) throw new Error('Missing hit.evt');

    // Mark it as missed, if needed:
    if (!(p[index].miss || p[index].hit)) {
      p[index].miss = hit;
    } else {
      // If it's unattached to a note:
      this.log('Pushing new miss: 702');
      this.log('hit.evt');
      this.log(hit.evt);
      this.log(hit);
      this.checkData.misses.push(hit);
    }

    hit.rc = rc;
    rc.draw();
    this.checkData.visualFeedbacks.push(rc);
  }

  stop() {
    const p = this.getVoicePointers().slice(-1)[0].stopPointer;
    this.log(p);
    this.markMissed({ performanceStartPointer: p });
    return this.calculateSkillsAndAccuracy();
  }

  calculateSkillsAndAccuracy() {
    const groups = Skills.processRhythm(this.voice);

    this.log('Groups:');
    this.log(groups);

    interface RhythmGroup {
      startPointer: Pointer;
      stopPointer: Pointer;
      details: RhythmSkillsGroup;
      n: number;
      average: number;
    }

    // Set start & stop pointers on each group:
    const groupObjs = groups.map((group, index): RhythmGroup => {
      const stopPointer = groups[index + 1]
        ? groups[index + 1].startPointer
        : group.startPointer.getNextMeasure();

      return {
        startPointer: group.startPointer,
        stopPointer: stopPointer.clone(),
        details: group,
        n: 0,
        average: 100,
      };
    });

    const after = Pointer.afterIndex;

    const voicePointers = this.getVoicePointers();

    // For each voice pointer:
    voicePointers.forEach((p) => {
      // What group am I in?
      const groupIndex = after(p.startPointer, groups, 'startPointer');
      const group = groupObjs[groupIndex];
      //  - Increment running average tally:
      if (group.n === 0) {
        group.average = 0;
      }
      if (p.hasOwnProperty('hit')) {
        //  - Calculate running average:
        this.log(`group.average: ${group.n} ${group.average} ${p.hit.accuracy.percent}`);
        this.log('p.hit:');
        this.log(p.hit);
        group.average = (group.n * group.average + p.hit.accuracy.percent) / ++group.n;
      } else {
        // It's a miss:
        group.average = (group.n * group.average + 0) / ++group.n;
      }
    }, this);

    // For any misses:
    this.checkData.misses.forEach((miss) => {
      // What group am I in?
      this.log('miss:');
      this.log(miss);
      let groupIndex;
      if (typeof miss === 'object' && typeof miss.evt !== 'undefined') {
        if (miss.evt.startTime < 0) {
          groupIndex = 0;
        } else {
          groupIndex = after(miss.evt.startPointer, groups, 'startPointer');
          // Might happen if someone plays before a pickup...
          if (groupIndex < 0) groupIndex = 0;
        }
      }

      if (typeof groupIndex === 'undefined') {
        console.log(
          'WARNING: Failed to mark group for this miss.\nMiss not being calculated -- degraded accuracy.'
        );
        console.log('miss: ');
        console.log(miss);
        return;
      }
      const group = groupObjs[groupIndex];
      this.log('groupIndex', groupIndex);
      this.log('group', group);
      //  - Increment running average tally:
      if (group.n === 0) {
        group.average = 0;
      }
      // It's a miss: calculate the running average:
      group.average = (group.n * group.average + 0) / ++group.n;
    }, this);

    groupObjs.forEach((o) => {
      this.log(`${o.average}\n`);
    }, this);

    this.log('Groups: (marked)');
    this.log(groupObjs);

    const sum = (a: number, b: number) => a + b;

    interface SortedGroups {
      [skillId: number]: RhythmGroup[];
    }

    // Now group the skills by ID:
    const sortedGroups: SortedGroups = groupObjs.reduce((accum, group) => {
      const key = group.details.id;
      if (!key) return accum;
      accum[key] = accum[key] || [];
      accum[key].push(group);
      return accum;
    }, {});

    // Now create an array of each skill & questions & percent:
    const skillsArray = Object.keys(sortedGroups)
      .map((key): RhythmGroup[] => sortedGroups[key])
      .map((arr) => {
        const skillAverager = utils.averageFactory();
        const skillAverage = arr.reduce((accum, group) => skillAverager(group.average), 0);
        const numberOfSoundedNotes = arr.map((g) => g.details.numberOfSoundedNotes).reduce(sum, 0);
        const n = arr.map((g) => g.n).reduce(sum, 0);
        const numberOfNotes = arr.map((g) => g.details.numberOfNotes).reduce(sum, 0);
        const questions = arr.length;
        const skill = arr[0].details.id;
        const textId = arr[0].details.textId;
        return {
          skill,
          textId,
          questions,
          numberOfNotes,
          numberOfSoundedNotes,
          n,
          average: skillAverage,
        };
      });

    // Calculate the average:
    const overallAverager = utils.averageFactory();
    const overallAverage = groupObjs.reduce(
      (accum, group) => overallAverager(group.average, group.details.numberOfNotes),
      0
    );

    this.log('Skills Array:');
    this.log(skillsArray);
    this.log(`average: ${overallAverage}`);
    return { average: overallAverage, skills: skillsArray };
  }

  markMissed(evt) {
    let rc;
    const p = this.getVoicePointers();
    // Helper function to find & disconnect any

    // Find any noteHolders that have been missed & mark them as such.
    for (let i = 0; i < p.length; i++) {
      if (p[i].hit || p[i].miss) continue;
      if (evt.performanceStartPointer.isGreaterThan(p[i].startPointer)) {
        p[i].miss = true;
        this.checkData.nextExpectedNote = i + 1;
        rc = new RhythmCheck(p[i].noteHolder, 'missed');
        rc.draw();
        this.checkData.visualFeedbacks.push(rc);
        this.checkData.omittedNotes++;
      } else {
        break;
      }
    }
  }

  calculateAccuracy(hit: Hit, evt: RecorderEventWithHit): Accuracy {
    // Accuracy is calculated as a factor of two scores:
    //   - The accuracy in seconds
    //   - The relative duration from the previous tap
    //     so that if a user is always a bit late,
    //     we sense that the rhythm is right, just behind.

    // Calculate accuracy in seconds:

    // Figure out how many seconds off we are:
    const differenceSeconds = hit.absDifferenceBeats * this.clock.beatSec;

    // Score it based on a quadratic curve -- peak is at
    // this.macPerfextToleranceSeconds, so if the error
    // is less than that, make it that.
    let x = Math.max(differenceSeconds, this.maxPerfectToleranceSeconds);

    // If it is greater than the max tolerance, we'd get a negative
    // value, so set it to the max tolerance:
    x = Math.min(x, this.toleranceSeconds);

    // b is the max deviation for a 100% score
    const b = this.maxPerfectToleranceSeconds; // probably 0.04
    // m is the deviation at which 0% is reached
    const m = this.toleranceSeconds; // probably 0.2

    // y = ( (x-0.2)/(0.2-0.04) )^2  -- Too demanding a curve!
    // y=-((x-0.04)/(0.2-0.04))^2 + 1 -- Much better!!!
    // Score it based on a falling quadratic curve
    let accuracySeconds = -((Math.abs(x - b) / (m - b)) ** 2) + 1;
    accuracySeconds = Math.min(accuracySeconds, 1);

    let accuracy = accuracySeconds;
    const accuracySecondsPercent = ~~(100 * accuracySeconds);

    // If the secondAccuracy is very good, or there's no
    // prior event -- leave it be.
    // Otherwise, calculate the beat ratio accuracy to

    let accuracyDeviation: undefined | number;
    if (accuracySeconds < 0.9 && this.checkData.events.length > 1) {
      // Calculate beat ratio accuracy:
      // distance to last tap vs expected distance
      // between hits

      const evtIndex = this.checkData.events.indexOf(evt);
      const lastEvt = this.checkData.events[evtIndex - 1];

      const noteIndex = hit.answerPointersIndex;
      const note = this.getVoicePointers()[noteIndex];
      const lastNote = this.getVoicePointers()[noteIndex - 1];
      // If last note hit && last event hit (they must match)
      if (
        evtIndex > 0 &&
        noteIndex > 0 &&
        lastEvt.hit &&
        lastEvt.hit.answerPointersIndex === noteIndex - 1
      ) {
        const expectedDuration = note.startPointer
          .clone()
          .subtract(lastNote.startPointer)
          .toBeats();
        const actualDuration = evt.performanceStartPointer
          .clone()
          .subtract(lastEvt.performanceStartPointer)
          .toBeats();
        accuracyDeviation = 1 - Math.abs(expectedDuration - actualDuration) / expectedDuration;
      }

      if (accuracyDeviation !== undefined) {
        accuracy = Math.max(accuracy, (2 * accuracy + 3 * accuracyDeviation) / 5);
      }
    }

    // Cast as percent:
    accuracy = ~~(accuracy * 100);
    //      this.log("  Accuracy Total: " + accuracy);
    if (accuracy < 0) {
      this.log('Accuracy error!');
      this.log('hit:');
      this.log(hit);
      this.log(`secondAccuracy: ${accuracySeconds}`);
      this.log(`differenceSeconds: ${differenceSeconds}`);
      this.log(`differenceBeats: ${hit.differenceBeats}`);
    }
    return {
      percent: accuracy,
      seconds: accuracySecondsPercent,
      deviation: accuracyDeviation ? ~~(100 * accuracyDeviation) : undefined,
    };
  }

  getVoicePointers() {
    if (typeof this.voice === 'undefined') {
      throw new Error('NaV: Voice must be defined to get pointers.');
    }
    if (
      typeof this.checkData.voicePointers !== 'undefined' &&
      this.checkData.voicePointers.length
    ) {
      return this.checkData.voicePointers;
    }

    this.checkData.voicePointers = this.voice.getPerformancePointersWithSilences();
    return this.checkData.voicePointers;
  }

  initTablas() {
    this.tablas = new TablaInstrument(this.clock);
  }

  setSounds(bool) {
    this.options.playSounds = bool;
  }

  playSound(evt: RecorderEventWithHit, hits: Hit[], beatTolerance: number) {
    if (!this.options.playSounds) return;
    const answerPointers = this.getVoicePointers();
    // If we're within tolerance of the next expected note, assume that:
    const expectedNote = answerPointers[this.checkData.nextExpectedNote];
    if (!expectedNote) {
      this.tablas.play(evt);
      return;
    }

    const beatDifference = Math.abs(
      expectedNote.startPointer.toBeats() - evt.startPointer.toBeats()
    );
    if (beatDifference < 2 * beatTolerance) {
      const nextAnswerPointer = answerPointers[hits[0].answerPointersIndex + 1];
      evt.stopPointer = evt.startPointer.clone().add(expectedNote.durationPointer);
      this.tablas.play(evt, nextAnswerPointer);
    } else {
      const hit = hits[0];
      const nextAnswerPointer = answerPointers[hit.answerPointersIndex + 1];
      this.tablas.play(evt, nextAnswerPointer);
    }
  }
}

// RhythmChecker.prototype.log = console.log.bind(console);
